import React, { useState, useEffect } from 'react';
import './DashboardPage.css';
import Layout from './layout';
import { getStudents, getStudentCourseResources, setStudentResourceAssignment } from './api/studentApi';
import { getTrainers, getTrainerCourses } from './api/trainerApi';
import { getCourses } from './api/courseApi';
import { enrollStudent, getEnrollments, markEnrollmentPaid } from './api/enrollmentApi';
import Confetti from 'react-confetti';

function DashboardPage() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseResources, setCourseResources] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [trainers, setTrainers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [assignTrainerId, setAssignTrainerId] = useState('');
  const [assignCourseId, setAssignCourseId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');
  const [studentPage, setStudentPage] = useState(1);
  const [studentQuery, setStudentQuery] = useState('');
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [studentLoading, setStudentLoading] = useState(false);
  const [trainerCourses, setTrainerCourses] = useState([]);
  const [trainerCoursesLoading, setTrainerCoursesLoading] = useState(false);
  const [courseResourcesLoading, setCourseResourcesLoading] = useState(false);
  const [markPaidLoadingIds, setMarkPaidLoadingIds] = useState(new Set());
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCelebrating, setShowCelebrating] = useState(false);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  // Fetch students (with pagination and search)
  const fetchStudents = async (opts = {}) => {
    setStudentLoading(true);
    const page = opts.page || studentPage;
    const query = opts.query !== undefined ? opts.query : studentQuery;
    try {
      const data = await getStudents({ page, query });
      const newStudents = data.students || data;
      if (opts.append) {
        setStudents(prev => [...prev, ...newStudents]);
      } else {
        setStudents(newStudents);
      }
      setHasMoreStudents(newStudents.length > 0);
      if (!opts.append && newStudents.length > 0) {
        setSelectedStudent(newStudents[0]);
      }
    } finally {
      setStudentLoading(false);
    }
  };

  // Fetch enrollments for selected student
  const fetchEnrollments = async (studentId) => {
    if (!studentId) {
      setEnrollments([]);
      return;
    }
    setEnrollmentsLoading(true);
    try {
      const data = await getEnrollments(studentId);
      setEnrollments(data.enrollments || []);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  // Fetch course resources for selected student and course
  const fetchCourseResources = async (studentId, courseId) => {
    if (!studentId || !courseId) {
      setCourseResources([]);
      return;
    }
    setCourseResourcesLoading(true);
    try {
      const data = await getStudentCourseResources(studentId, courseId);
      setCourseResources(data);
    } finally {
      setCourseResourcesLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents({ page: 1, query: '' });
    getTrainers().then(data => setTrainers(data.trainers || data));
    getCourses().then(data => setCourses(data.courses || data));
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchEnrollments(selectedStudent.id);
    } else {
      setEnrollments([]);
    }
  }, [selectedStudent]);

  // When assignTrainerId changes in the modal, fetch that trainer's courses
  useEffect(() => {
    if (assignTrainerId) {
      setTrainerCoursesLoading(true);
      getTrainerCourses(assignTrainerId).then(data => {
        setTrainerCourses(data.courses || data);
      }).finally(() => {
        setTrainerCoursesLoading(false);
      });
    } else {
      setTrainerCourses([]);
      setTrainerCoursesLoading(false);
    }
  }, [assignTrainerId]);

  // When selectedCourse changes, fetch resources for that course and student
  useEffect(() => {
    if (selectedStudent && selectedCourse) {
      fetchCourseResources(selectedStudent.id, selectedCourse.id);
    } else {
      setCourseResources([]);
    }
  }, [selectedStudent, selectedCourse]);

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setSelectedCourse(null);
    setCourseResources([]);
  };

  const handleLoadMoreStudents = () => {
    const nextPage = studentPage + 1;
    setStudentPage(nextPage);
    fetchStudents({ page: nextPage, append: true });
  };

  const handleStudentSearch = (e) => {
    const value = e.target.value;
    setStudentQuery(value);
    setStudentPage(1);
    fetchStudents({ page: 1, query: value });
  };

  const handleAssignToCourse = () => {
    setAssignTrainerId('');
    setAssignCourseId('');
    setAssignError('');
    setAssignSuccess('');
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    setAssignLoading(true);
    setAssignError('');
    setAssignSuccess('');
    try {
      await enrollStudent({
        student_id: selectedStudent.id,
        course_id: assignCourseId,
        trainer_id: assignTrainerId,
      });
      setAssignSuccess('Student assigned to course successfully!');
      setShowAssignModal(false);
      // Refresh enrollments after assignment
      fetchEnrollments(selectedStudent.id);
    } catch (err) {
      setAssignError('Failed to assign student to course.');
    } finally {
      setAssignLoading(false);
    }
  };

  // Filter enrollments for the selected student (no longer needed, but kept for safety)
  const studentEnrollments = enrollments || [];

  // Handle resource assignment toggle
  const handleResourceAssignmentToggle = async (resource) => {
    if (!selectedStudent || !selectedCourse) return;
    const newAssigned = !resource.assigned;
    // Optimistically update UI
    setCourseResources(prev => prev.map(r => r.id === resource.id ? { ...r, assigned: newAssigned } : r));
    try {
      await setStudentResourceAssignment({
        student_id: selectedStudent.id,
        resource_id: resource.id,
        course_id: selectedCourse.id,
        is_assigned: newAssigned
      });
      // Optionally, reload resources from server for consistency
      fetchCourseResources(selectedStudent.id, selectedCourse.id);
    } catch (err) {
      // Revert on error
      setCourseResources(prev => prev.map(r => r.id === resource.id ? { ...r, assigned: resource.assigned } : r));
      alert('Failed to update resource assignment.');
    }
  };

  // Handler: mark a single enrollment as paid
  const handleMarkPaid = async (enrollment) => {
    setMarkPaidLoadingIds(prev => new Set(prev).add(enrollment.id));
    try {
      await markEnrollmentPaid(enrollment.id);
      setShowCelebrating(true);
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
        setShowCelebrating(false);
      }, 7000);
      fetchEnrollments(selectedStudent.id);
    } catch (err) {
      alert("Try again. Something went wrong. Ask your tech monkey to investigate this further.")
    } finally {
      setMarkPaidLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(enrollment.id);
        return newSet;
      });
    }
  };

  return (
    <Layout>
      {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={1200} />}
      {showCelebrating && (
        <img
          src="/img/celebrating.png"
          alt="Celebrating"
          className="thumbs-up-animation"
        />
      )}
      <div className="container-fluid p-4">
        <div className="row">
          {/* Students List */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-header fw-bold">Students</div>
              <div className="p-2">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Search students by name..."
                  value={studentQuery}
                  onChange={handleStudentSearch}
                  disabled={studentLoading}
                />
              </div>
              <ul className="list-group list-group-flush">
                {students.map((student) => (
                  <li
                    key={student.id}
                    className={`list-group-item list-group-item-action ${
                      selectedStudent?.id === student.id ? 'active' : ''
                    }`}
                    onClick={() => handleStudentSelect(student)}
                    style={{ cursor: 'pointer' }}
                  >
                    {student.User?.full_name || student.full_name}
                  </li>
                ))}
              </ul>
              <div className="p-2">
                <button
                  className="btn btn-outline-primary w-100"
                  onClick={handleLoadMoreStudents}
                  disabled={!hasMoreStudents || studentLoading}
                >
                  {studentLoading ? 'Loading...' : hasMoreStudents ? 'Load More' : 'No More Students'}
                </button>
              </div>
            </div>
          </div>

          {/* Student Details and Courses */}
          <div className="col-md-4">
            {selectedStudent && (
              <div className="card">
                <div className="card-header fw-bold">
                  {(selectedStudent.User?.full_name || selectedStudent.full_name) + "'s Courses"}
                </div>
                {enrollmentsLoading ? (
                  <div className="text-center p-4">
                    <img src="/img/loading.png" alt="Loading courses..." style={{ width: '300px' }} />
                  </div>
                ) : (
                  <>
                    <ul className="list-group list-group-flush">
                      {studentEnrollments.length === 0 && (
                        <li className="list-group-item text-muted">No courses assigned.</li>
                      )}
                      {studentEnrollments.map((enrollment) => {
                        const course = courses.find(c => c.id === enrollment.course.id) || enrollment;
                        const trainer = trainers.find(t => t.id === enrollment.trainer_id);
                        const paid = enrollment.paid;
                        const enrolledAt = enrollment.enrolled_at;
                        const isLoading = markPaidLoadingIds.has(enrollment.id);
                        return (
                          <li
                            key={enrollment.id}
                            className={`list-group-item list-group-item-action ${
                              selectedCourse?.id === course?.id ? 'active' : ''
                            }`}
                            onClick={() => setSelectedCourse(course)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <span>{course ? (course.name || course.title) : 'Unknown Course'}</span>
                              <div className="d-flex align-items-center gap-2">
                                <span
                                  className={`badge rounded-pill ms-2 ${paid ? 'bg-success' : 'bg-danger'}`}
                                  style={{ fontSize: '0.8em' }}
                                >
                                  {paid ? 'paid' : 'not paid'}
                                </span>
                                {!paid && (
                                  <button
                                    key={enrollment.id}
                                    className="btn btn-sm btn-outline-success"
                                    onClick={e => { e.stopPropagation(); handleMarkPaid(enrollment); }}
                                    disabled={isLoading}
                                  >
                                    {isLoading ? 'Marking...' : 'Mark as Paid'}
                                  </button>
                                )}
                              </div>
                            </div>
                            <small className="text-muted">
                              Trainer: {trainer ? (trainer.User?.full_name || trainer.full_name) : 'Unknown Trainer'}
                            </small>
                            <br />
                            {enrolledAt && (
                              <small className="text-secondary">Enrolled: {new Date(enrolledAt).toLocaleDateString()}</small>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <div className="p-3">
                      <button className="btn btn-link p-0" onClick={handleAssignToCourse}>
                        + Add to course
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Course Resources */}
          <div className="col-md-4">
            {selectedCourse && (
              <div className="card">
                <div className="card-header fw-bold">Resources</div>
                <ul className="list-group list-group-flush">
                  {courseResourcesLoading ? (
                    <li className="list-group-item">
                      <img src="/img/checking.png" />
                    </li>
                  ) : courseResources.length === 0 ? (
                    <li className="list-group-item text-muted">No resources found.</li>
                  ) : (
                    courseResources.map(resource => (
                      <li key={resource.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>{resource.title}</span>
                        <div className="form-check form-switch">
                          <label className="form-check-label ms-2">
                            {resource.assigned ? 'Assigned' : 'Not Assigned'}
                          </label>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={!!resource.assigned}
                            onChange={() => handleResourceAssignmentToggle(resource)}
                          />
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Assign to Course Modal */}
        {showAssignModal && (
          <div className="modal show d-block" tabIndex="-1" role="dialog" style={{background: 'rgba(0,0,0,0.3)'}}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Assign Student to Course</h5>
                  <button type="button" className="btn-close" onClick={() => setShowAssignModal(false)} aria-label="Close"></button>
                </div>
                <div className="modal-body">
                  {assignError && <div className="alert alert-danger">{assignError}</div>}
                  {assignSuccess && <div className="alert alert-success">{assignSuccess}</div>}
                  <div className="mb-3">
                    <label className="form-label">Trainer</label>
                    <select className="form-select" value={assignTrainerId} onChange={e => setAssignTrainerId(e.target.value)} required>
                      <option value="">Select Trainer</option>
                      {trainers.map(trainer => (
                        <option key={trainer.id} value={trainer.id}>
                          {trainer.User?.full_name || trainer.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {assignTrainerId && (
                    <div className="mb-3">
                      <label className="form-label">Course</label>
                      <select className="form-select" value={assignCourseId} onChange={e => setAssignCourseId(e.target.value)} required>
                        {trainerCoursesLoading ? (
                          <option value="" selected={true}>loading...</option>
                        ) : (
                          <option value="">Select Course</option>
                        )}
                        {!trainerCoursesLoading && trainerCourses.map(course => (
                          <option key={course.id} value={course.id}>
                            {course.name || course.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleAssign} disabled={!assignTrainerId || !assignCourseId || assignLoading}>
                    {assignLoading ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default DashboardPage; 